import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Plus,
  UserPlus,
  Search,
  X,
  Check,
  Loader2,
} from 'lucide-react';
import { Card, Button, Input, Textarea, Select } from '@azyntor-labs/nexus-ui';
import { studentGroupsApi, studentsApi } from '@/api/endpoints';
import { toast } from '@/utils/toast';
import { useDebounce } from '@/hooks/useDebounce';

interface Group {
  id: string;
  name: string;
  description?: string;
  group_type: string;
  member_count: number;
  grade_level?: string;
  subject?: string;
  created_at: string;
}

interface Student {
  id: string;
  name: string;
  email: string;
}

export const StudentGroupsPage = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [searchQuery] = useState('');
  useDebounce(searchQuery, 300);

  // Fetch groups
  const { data: groupsData, isLoading } = useQuery({
    queryKey: ['student-groups'],
    queryFn: async () => {
      const response = await studentGroupsApi.list(true);
      return response.data;
    },
  });

  const groups = groupsData?.data || [];

  return (
    <div className="min-h-screen bg-[#0a0a0f] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Student Groups</h1>
            <p className="text-gray-400">Manage cohorts for bulk enrollment</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            leftIcon={<Plus className="w-4 h-4" />}
            className="bg-teal-500 hover:bg-teal-600 text-white"
          >
            Create Group
          </Button>
        </div>

        {/* Groups List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
          </div>
        ) : groups.length === 0 ? (
          <Card className="bg-[#12121a]/80 border-white/[0.06] p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Groups Yet</h3>
            <p className="text-gray-400 mb-4">Create your first student group to get started</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              leftIcon={<Plus className="w-4 h-4" />}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              Create Group
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.map((group: Group) => (
              <Card key={group.id} className="bg-[#12121a]/80 border-white/[0.06] p-6 hover:border-teal-400/30 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-1">{group.name}</h3>
                    <p className="text-sm text-gray-400">{group.group_type}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        setSelectedGroup(group);
                        setShowAddMembersModal(true);
                      }}
                      size="sm"
                      variant="ghost"
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {group.description && (
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2">{group.description}</p>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-teal-400" />
                    <span className="text-sm text-white font-medium">{group.member_count} students</span>
                  </div>
                  <Button
                    onClick={() => setSelectedGroup(group)}
                    size="sm"
                    variant="ghost"
                    className="text-teal-400 hover:text-teal-300"
                  >
                    View Members
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create Group Modal */}
        {showCreateModal && (
          <CreateGroupModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              queryClient.invalidateQueries({ queryKey: ['student-groups'] });
            }}
          />
        )}

        {/* Add Members Modal */}
        {showAddMembersModal && selectedGroup && (
          <AddMembersModal
            group={selectedGroup}
            onClose={() => {
              setShowAddMembersModal(false);
              setSelectedGroup(null);
            }}
            onSuccess={() => {
              setShowAddMembersModal(false);
              setSelectedGroup(null);
              queryClient.invalidateQueries({ queryKey: ['student-groups'] });
            }}
          />
        )}
      </div>
    </div>
  );
};

// Create Group Modal Component
const CreateGroupModal = ({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    group_type: 'cohort',
    grade_level: '',
    subject: '',
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await studentGroupsApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Group created successfully');
      onSuccess();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to create group';
      toast.error(message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="bg-[#12121a] border-white/[0.08] p-6 max-w-lg w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Create Student Group</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Group Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., CEH Batch Dec 2025"
            required
          />

          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Optional description"
            rows={3}
          />

          <Select
            label="Group Type"
            value={formData.group_type}
            onChange={(e) => setFormData({ ...formData, group_type: e.target.value })}
            options={[
              { value: 'cohort', label: 'Cohort' },
              { value: 'class', label: 'Class' },
              { value: 'campaign', label: 'Campaign' },
              { value: 'department', label: 'Department' },
              { value: 'custom', label: 'Custom' },
            ]}
          />

          <Input
            label="Grade Level (Optional)"
            value={formData.grade_level}
            onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
            placeholder="e.g., 10th Grade"
          />

          <Input
            label="Subject (Optional)"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            placeholder="e.g., Cybersecurity"
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="ghost"
              className="flex-1 text-gray-400"
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
              disabled={createMutation.isPending}
              leftIcon={createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Group'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

// Add Members Modal Component
const AddMembersModal = ({ group, onClose, onSuccess }: { group: Group; onClose: () => void; onSuccess: () => void }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Search students
  const { data: searchData, isLoading: isSearching } = useQuery({
    queryKey: ['students-search', debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) return { data: { students: [] } };
      const response = await studentsApi.search({ q: debouncedSearch, per_page: 20 });
      return response.data;
    },
    enabled: debouncedSearch.length >= 2,
  });

  const students: Student[] = searchData?.data?.students || [];

  const addMembersMutation = useMutation({
    mutationFn: async (studentIds: string[]) => {
      const response = await studentGroupsApi.addMembers(group.id, studentIds);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Students added to group');
      onSuccess();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to add students';
      toast.error(message);
    },
  });

  const handleSubmit = () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    addMembersMutation.mutate(selectedStudents);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="bg-[#12121a] border-white/[0.08] p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">Add Students to {group.name}</h2>
            <p className="text-sm text-gray-400 mt-1">Search and select students to add</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search students by name or email..."
              className="w-full pl-10 pr-4 py-3 bg-[#0a0a0f] border border-white/[0.08] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-teal-400/50"
            />
          </div>
        </div>

        {searchQuery.length >= 2 && (
          <div className="mb-4 space-y-2">
            {isSearching ? (
              <div className="text-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-teal-400 mx-auto" />
              </div>
            ) : students.length === 0 ? (
              <p className="text-center text-gray-400 py-4">No students found</p>
            ) : (
              students.map((student) => (
                <label
                  key={student.id}
                  className="flex items-center gap-3 p-3 bg-[#0a0a0f] border border-white/[0.06] rounded-lg hover:border-teal-400/30 cursor-pointer transition-all"
                >
                  <input
                    type="checkbox"
                    checked={selectedStudents.includes(student.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStudents([...selectedStudents, student.id]);
                      } else {
                        setSelectedStudents(selectedStudents.filter((id) => id !== student.id));
                      }
                    }}
                    className="w-4 h-4 text-teal-500"
                  />
                  <div>
                    <p className="text-white font-medium">{student.name}</p>
                    <p className="text-sm text-gray-400">{student.email}</p>
                  </div>
                </label>
              ))
            )}
          </div>
        )}

        {selectedStudents.length > 0 && (
          <div className="mb-4 p-3 bg-teal-500/10 border border-teal-500/20 rounded-lg">
            <p className="text-sm text-teal-400">
              {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''} selected
            </p>
          </div>
        )}

        <div className="flex gap-3 pt-4">
          <Button
            onClick={onClose}
            variant="ghost"
            className="flex-1 text-gray-400"
            disabled={addMembersMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
            disabled={addMembersMutation.isPending || selectedStudents.length === 0}
            leftIcon={addMembersMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          >
            {addMembersMutation.isPending ? 'Adding...' : `Add ${selectedStudents.length} Student${selectedStudents.length !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </Card>
    </div>
  );
};
